import React, { useState } from 'react';
import Icon from '../Icon';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { panelCls, inputCls, labelCls, primaryBtn, ghostBtn, SectionHead, Note } from './ui';

const dangerBtn = 'h-11 px-space-lg rounded-lg bg-error text-on-error font-label-md text-label-md uppercase tracking-wider font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-space-xs';

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const hasPassword = user?.has_password !== false;

  const [stage, setStage] = useState('idle'); // idle | confirm
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [healthStage, setHealthStage] = useState('idle'); // idle | confirm | done
  const [healthBusy, setHealthBusy] = useState(false);

  const deleteHealthData = async () => {
    setHealthBusy(true);
    try {
      await api.delete('/auth/me/health-data');
      setHealthStage('done');
    } catch {
      setHealthStage('idle');
    } finally {
      setHealthBusy(false);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/me/delete', { confirm, password: hasPassword ? password : null });
      await logout();
      window.location.href = '/';
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'We could not delete the account. Please try again.');
      setBusy(false);
    }
  };

  return (
    <section className="w-full px-margin-mobile lg:px-margin pb-space-3xl" data-testid="account-settings">
      <div className={panelCls}>
        <SectionHead eyebrow="Account & Privacy" title="Your data, your choice" />
        <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed -mt-space-sm">
          You can remove your health information on its own, or delete your whole account.
        </p>

        {/* Health data only */}
        <div className="mt-space-lg p-space-lg rounded-lg bg-surface-container-low border border-surface-container" data-testid="delete-health-data-block">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
            <div>
              <p className="font-title-md text-title-md text-outline font-bold mb-space-xs">Delete my health data</p>
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                Removes your health metrics, medication records, dose logs and health-data consent. Your account,
                saved guides and habits stay exactly as they are.
              </p>
            </div>
            {healthStage === 'idle' ? (
              <button type="button" data-testid="delete-health-data-button" onClick={() => setHealthStage('confirm')} className={ghostBtn}>
                Delete health data
              </button>
            ) : null}
          </div>

          {healthStage === 'confirm' ? (
            <div className="mt-space-md p-space-md rounded-lg bg-primary-container border-l-4 border-surface-tint" data-testid="delete-health-data-confirm">
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-md">
                Delete all your health metrics, medication records and dose logs? This cannot be undone.
              </p>
              <div className="flex flex-wrap gap-space-sm">
                <button type="button" data-testid="delete-health-data-cancel" onClick={() => setHealthStage('idle')} className={ghostBtn}>Keep my data</button>
                <button type="button" data-testid="delete-health-data-confirm-button" onClick={deleteHealthData} disabled={healthBusy} className={dangerBtn}>
                  {healthBusy ? 'Deleting…' : 'Yes, delete my health data'}
                </button>
              </div>
            </div>
          ) : null}

          {healthStage === 'done' ? (
            <Note>Your health data has been deleted.</Note>
          ) : null}
        </div>

        {/* Whole account */}
        <div className="mt-space-md p-space-lg rounded-lg bg-surface-container-low border border-surface-container" data-testid="delete-account-block">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-space-md">
            <div>
              <p className="font-title-md text-title-md text-outline font-bold mb-space-xs">Delete my account</p>
              <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed">
                Permanently deletes your account and everything in it: your profile, saved guides, habits and logs,
                weekly priorities, reflections, goals, health metrics, medication records and consent records.
              </p>
            </div>
            {stage === 'idle' ? (
              <button type="button" data-testid="delete-account-button" onClick={() => setStage('confirm')} className={ghostBtn}>
                Delete my account
              </button>
            ) : null}
          </div>

          {stage === 'confirm' ? (
            <div className="mt-space-md p-space-lg rounded-lg bg-primary-container border-l-4 border-surface-tint" data-testid="delete-account-confirm">
              <div className="flex items-start gap-space-md">
                <Icon name="verified" className="text-surface-tint mt-1 text-[24px] shrink-0" />
                <div className="flex-1">
                  <p className="font-title-md text-title-md text-outline uppercase tracking-wider mb-space-xs">This cannot be undone</p>
                  <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-md">
                    Everything listed above will be permanently deleted and you will be signed out. If you only want to
                    remove your health information, use “Delete my health data” instead.
                  </p>

                  {hasPassword ? (
                    <div className="mb-space-md max-w-sm">
                      <label className={labelCls} htmlFor="delete-password">Your password</label>
                      <input
                        id="delete-password"
                        data-testid="delete-account-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={inputCls}
                        placeholder="Enter your password"
                      />
                    </div>
                  ) : null}

                  <div className="mb-space-md max-w-sm">
                    <label className={labelCls} htmlFor="delete-confirm">Type DELETE to confirm</label>
                    <input
                      id="delete-confirm"
                      data-testid="delete-account-confirm-input"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className={inputCls}
                      placeholder="DELETE"
                    />
                  </div>

                  <div className="flex flex-wrap gap-space-sm">
                    <button
                      type="button"
                      data-testid="delete-account-cancel"
                      onClick={() => { setStage('idle'); setConfirm(''); setPassword(''); setError(''); }}
                      className={primaryBtn}
                    >
                      Keep my account
                    </button>
                    <button
                      type="button"
                      data-testid="delete-account-confirm-button"
                      onClick={deleteAccount}
                      disabled={busy || confirm.trim().toUpperCase() !== 'DELETE'}
                      className={dangerBtn}
                    >
                      {busy ? 'Deleting…' : 'Permanently delete my account'}
                    </button>
                  </div>

                  {error ? (
                    <p className="mt-space-md font-body-md text-body-md text-error" data-testid="delete-account-error">{error}</p>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
